// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { MoreHorizontal, Power, PowerOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { cn } from "@/lib/utils";

interface OctoRuleActionsMenuProps {
  isActive: boolean;
  onToggleActive: () => void;
  triggerClassName?: string;
}

export function OctoRuleActionsMenu({ isActive, onToggleActive, triggerClassName }: OctoRuleActionsMenuProps) {
  const { t } = useTranslation("back-office");
  const { hasPermission } = useAuth();
  if (!hasPermission(PAGE_PERMISSIONS.octoRulesEdit)) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className={cn("h-8 w-8", triggerClassName)}>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={-32} className="dropdown-overlap">
        <DropdownMenuItem
          onSelect={onToggleActive}
          className={isActive ? "text-destructive focus:text-destructive" : ""}
        >
          {isActive ? (
            <>
              <PowerOff className="h-4 w-4 text-destructive" />
              {t("octoRules.list.actions.deactivate")}
            </>
          ) : (
            <>
              <Power className="h-4 w-4" />
              {t("octoRules.list.actions.activate")}
            </>
          )}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
